import { readLastIdentifier, rememberIdentifier } from "@kora/kit/entry/remembered-login";
import { TextField } from "@kora/kit/primitives/TextField/TextField";
import { useEffect, useState } from "react";
import { ClientNotice, Failure, GhostButton, Loading, Messages, Note, PasswordToggle, PrimaryButton, Screen } from "../components/Entry";
import { UNVERIFIED_ADDRESS, copy } from "../lib/copy";
import { errorsFor, go, hasGroup, loginChallenge, nodeNamed, submitNodes, useFlow, valueOf } from "../lib/flow";
import { browserFlowUrl, logoutUrl, redirectFrom, type Flow, type SuccessBody } from "../lib/kratos";
import { href, leave, navigate } from "../lib/router";

export function Login({ query }: { query: URLSearchParams }) {
  const challenge = query.get("login_challenge") ?? undefined;
  // OAuth clients land on registration first, as Playtron's portal always has, unless they ask for sign-in.
  const registrationFirst = !query.get("flow") && challenge && query.get("prompt") !== "login";
  // Kratos picks an OAuth sign-in's first step: straight back to Hydra when the session already covers it, otherwise
  // a flow it redirects here with (?flow=).
  const startAtKratos = !registrationFirst && challenge && !query.get("flow");
  useEffect(() => {
    if (registrationFirst) navigate(href("/registration", { login_challenge: challenge, return_to: query.get("return_to") }), { replace: true });
    else if (startAtKratos)
      leave(
        browserFlowUrl("login", {
          login_challenge: challenge,
          return_to: query.get("return_to") ?? undefined,
          aal: query.get("aal") ?? undefined,
          refresh: query.get("refresh") ?? undefined,
        }),
      );
  }, [registrationFirst, startAtKratos, challenge, query]);
  if (registrationFirst || startAtKratos) return <Loading />;
  return <LoginFlow query={query} />;
}

function LoginFlow({ query }: { query: URLSearchParams }) {
  const state = useFlow("login", "/login", query, {
    return_to: query.get("return_to") ?? undefined,
    login_challenge: query.get("login_challenge") ?? undefined,
    aal: query.get("aal") ?? undefined,
    refresh: query.get("refresh") ?? undefined,
    via: query.get("via") ?? undefined,
  });
  const { flow } = state;
  if (!flow) return state.failure ? <Screen title="Sign in"><Failure text={state.failure} /></Screen> : <Loading />;
  const done = (body: SuccessBody) => go(redirectFrom(body) || flow.return_to || "/");
  if (hasGroup(flow, "code") && !hasGroup(flow, "password")) return <SecondFactor flow={flow} state={state} onDone={done} />;
  return <Password flow={flow} state={state} onDone={done} />;
}

type State = ReturnType<typeof useFlow>;

function Password({ flow, state, onDone }: { flow: Flow; state: State; onDone: (b: SuccessBody) => void }) {
  const [identifier, setIdentifier] = useState(() => valueOf(flow, "identifier") || readLastIdentifier());
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const messages = flow.ui.messages ?? [];
  const failed = messages.some(m => m.type === "error");
  const unverified = messages.some(m => m.id === UNVERIFIED_ADDRESS);
  const challenge = loginChallenge(flow);

  return (
    <Screen title={flow.refresh ? "Confirm it’s you" : "Sign in"}>
      <ClientNotice client={flow.oauth2_login_request?.client} />
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          rememberIdentifier(identifier);
          void state.submit({ method: "password", identifier: identifier.trim(), password }, onDone);
        }}
      >
        <TextField
          label="Email"
          inputType="email"
          inputProps={{ autoComplete: "username", name: "identifier", readOnly: flow.refresh }}
          value={identifier}
          onChange={setIdentifier}
          error={errorsFor(flow, "identifier").map(copy)[0]}
          size="lg"
        />
        <TextField
          label="Password"
          inputType={shown ? "text" : "password"}
          inputProps={{ autoComplete: "current-password", name: "password" }}
          value={password}
          onChange={setPassword}
          error={errorsFor(flow, "password").map(copy)[0]}
          size="lg"
        />
        <PasswordToggle shown={shown} onToggle={() => setShown(!shown)} />
        {failed && (
          <div className="entry-login-error">
            <Messages messages={messages} />
            {unverified ? (
              <GhostButton size="sm" onClick={() => navigate(href("/verification", { email: identifier.trim() }))}>
                Verify email
              </GhostButton>
            ) : (
              <GhostButton size="sm" onClick={() => navigate(href("/recovery", { email: identifier.trim(), return_to: flow.return_to }))}>
                Forgot password?
              </GhostButton>
            )}
          </div>
        )}
        {!failed && <Messages messages={messages} />}
        <Failure text={state.failure} />
        <PrimaryButton pending={state.pending}>Sign in</PrimaryButton>
        {!flow.refresh && (
          <div className="entry-login-links">
            <span />
            <GhostButton onClick={() => navigate(href("/registration", { return_to: flow.return_to, login_challenge: challenge }))}>Create account</GhostButton>
          </div>
        )}
      </form>
    </Screen>
  );
}

/** Kratos's email-code second factor: first send the code to an address, then enter it. */
function SecondFactor({ flow, state, onDone }: { flow: Flow; state: State; onDone: (b: SuccessBody) => void }) {
  const [code, setCode] = useState("");
  const addresses = submitNodes(flow, "code").filter(n => n.attributes.name === "address");
  // Kratos lists one send button per address until a code is out; after that the flow is in sent_email.
  const sent = flow.state === "sent_email" || (addresses.length === 0 && Boolean(nodeNamed(flow, "code")));
  const address = valueOf(flow, "address") || (addresses[0]?.attributes.value as string | undefined) || "";
  const signOut = async () => go((await logoutUrl(window.location.origin + "/login")) ?? "/login");

  return (
    <Screen title={sent ? "Check your email" : "Confirm it’s you"}>
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          if (sent) void state.submit({ method: "code", code }, onDone);
          else void state.submit({ method: "code", address, resend: "code" }, onDone);
        }}
      >
        {sent ? (
          <>
            <Note>Enter the code we sent to {address || "your email"}.</Note>
            <TextField
              label="Sign-in code"
              value={code}
              onChange={value => setCode(value.replace(/\s/g, "").slice(0, 8))}
              inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", name: "code" }}
              error={errorsFor(flow, "code").map(copy)[0]}
              size="lg"
            />
          </>
        ) : (
          <Note>We’ll email a sign-in code to {address || "your address"}.</Note>
        )}
        <Messages messages={(flow.ui.messages ?? []).filter(m => !(sent && m.id === 1010014))} />
        <Failure text={state.failure} />
        <PrimaryButton pending={state.pending}>{sent ? "Continue" : "Email me a code"}</PrimaryButton>
        {sent && (
          <GhostButton onClick={() => void state.submit({ method: "code", address, resend: "code" }, onDone)} disabled={state.pending}>
            Send another code
          </GhostButton>
        )}
        <GhostButton onClick={() => void signOut()}>Use another account</GhostButton>
      </form>
    </Screen>
  );
}
